import type { RouteLocationRaw } from 'vue-router';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import * as z from 'zod/v4-mini';
import { readUserSessionRecord, useOnboardingPopup, useUserSessionRecord } from '#layers/access/public';
import { userStorageKey } from '#layers/base/public';
import { settingsOverlayPath } from '#layers/global-settings/public';
import { useWorkspaceStore } from '#layers/workspace/public';

export function onboardingProgressKey(userId: string): string {
	return userStorageKey(userId, 'onboarding-progress');
}

export const onboardingStatuses = ['active', 'dismissed', 'completed'] as const;

export const onboardingSteps = ['workspace', 'data-source', 'connect'] as const;
export type OnboardingStepId = (typeof onboardingSteps)[number];

export const onboardingProgressSchema = z.object({
	status: z.enum(onboardingStatuses),
	currentStep: z.enum(onboardingSteps),
	workspaceSlug: z.string(), // slug of the workspace created during onboarding
});

export type OnboardingProgress = z.infer<typeof onboardingProgressSchema>;

/**
 * Used by routing middleware that only needs a one-shot value and must
 * not leave a storage listener behind on every navigation
 */
export function readOnboardingProgress(userId: string): OnboardingProgress | null {
	return readUserSessionRecord(onboardingProgressKey(userId), onboardingProgressSchema, null);
}

export function useOnboarding() {
	const { currentWorkspaceSlug } = storeToRefs(useWorkspaceStore());
	const { start: startOnboardingPopup } = useOnboardingPopup();

	// Authoritative session-backed record, keyed to the current user so it can't
	// bleed across a logout/login in the same tab. `null` until `start()` seeds it.
	const { state: progress, set, update } = useUserSessionRecord(
		onboardingProgressKey,
		onboardingProgressSchema,
		null,
	);

	/**
	 * Enter onboarding at the workspace step. Called when the user lands on the workspace
	 * step so the routing guard pins them to the flow before a workspace exists (the
	 * slug is filled in later by `start()`). No-op once any record exists — in
	 * progress, dismissed, or completed — so a returning user is never re-trapped.
	 */
	function begin() {
		startOnboardingPopup();

		if (progress.value !== null) return;

		set({
			status: 'active',
			currentStep: 'workspace',
			workspaceSlug: '',
		});
	}

	/**
	 * Create the onboarding record for the given workspace. Called from the workspace step
	 * after the workspace is hydrated. A freshly created workspace is authoritative, so a
	 * stale record from a prior attempt in the same tab is replaced; only a retried
	 * submit of the same workspace is a no-op.
	 */
	function start(workspaceSlug: string) {
		startOnboardingPopup();

		if (progress.value?.workspaceSlug === workspaceSlug) return;

		set({
			status: 'active',
			currentStep: 'data-source',
			workspaceSlug,
		});
	}

	function setField<Key extends keyof OnboardingProgress>(key: Key, value: OnboardingProgress[Key]) {
		update((current) => (current && current[key] !== value ? { ...current, [key]: value } : current));
	}

	function setCurrentStep(step: OnboardingStepId) {
		setField('currentStep', step);
	}

	function complete() {
		startOnboardingPopup();

		setField('status', 'completed');
	}

	// String path (not a named route) since there may be no current workspace to
	// scope a route object to; `/~/...` resolves the backdrop workspace itself.
	const dashboard: RouteLocationRaw = settingsOverlayPath('organization/workspaces');

	const workspaceHome = computed<RouteLocationRaw>(() =>
		currentWorkspaceSlug.value
			? { name: 'workspace-home', params: { workspace: currentWorkspaceSlug.value } }
			: dashboard,
	);

	function dataSourceView(source: string | null): RouteLocationRaw {
		return currentWorkspaceSlug.value && source
			? { name: 'data-model.source', params: { workspace: currentWorkspaceSlug.value, source } }
			: workspaceHome.value;
	}

	return {
		progress,
		begin,
		start,
		setCurrentStep,
		complete,
		dashboard,
		workspaceHome,
		dataSourceView,
	};
}
